import {
  Flex,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderTrack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  KeyTermUnit,
  MarketDataSourceType,
} from '@client/graphql/__generated__/types';
import isNil from 'lodash/isNil';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { formatAgentRecommendation, formatRangeValue } from '../common.utils';
import { OutOfRangeSection } from './OutOfRangeSection';
import { RangeDot } from './RangeDot';
import { RangeSliderThumbWithTooltip } from './RangeSliderThumbWithTooltip';
import {
  calculateDotPosition,
  calculateRangeValues,
  roundRangeLabels,
} from './range.utils';

interface KeyTermsRangeChartProps {
  anchor?: {
    value: number;
    label: string;
  };
  dotColor?: string;
  fullRange: [number, number];
  getRangeTrackTooltipContent?: ({
    isOpen,
    children,
  }: {
    isOpen: boolean;
    children: React.ReactNode;
  }) => React.ReactNode;
  highlightedRange?: [number, number];
  showValue?: boolean;
  source?: MarketDataSourceType;
  unit: KeyTermUnit;
  value?: number | null;
}

export const KeyTermsRangeChart = ({
  anchor,
  dotColor = 'gray.500',
  fullRange,
  getRangeTrackTooltipContent,
  highlightedRange,
  showValue = false,
  source,
  unit,
  value,
}: KeyTermsRangeChartProps) => {
  const isMobile = useIsMobile();
  const { difference, minRangeValue, maxRangeValue } = calculateRangeValues(
    fullRange,
    unit
  );

  const formattedValue = formatAgentRecommendation({ value, unit });

  const rangeValues = [
    minRangeValue,
    fullRange[0],
    fullRange[0] + difference,
    fullRange[0] + difference * 2,
    fullRange[1],
    fullRange[1] + difference,
  ].map((label) => roundRangeLabels(label, unit));

  const rangeLabels = rangeValues.map((value) =>
    formatRangeValue(value, unit, isMobile)
  );

  const dotPosition = !isNil(value)
    ? calculateDotPosition(
        value,
        rangeValues[0],
        rangeValues[rangeValues.length - 1],
        false
      )
    : '0';

  const anchorDotPosition = !isNil(anchor?.value)
    ? calculateDotPosition(
        anchor.value,
        rangeValues[0],
        rangeValues[rangeValues.length - 1],
        false
      )
    : '0';

  const valueGreaterThanRange =
    !isNil(value) && value > rangeValues[rangeValues.length - 1];
  const valueLessThanRange = !isNil(value) && value < rangeValues[0];
  const valueInRange = !valueGreaterThanRange && !valueLessThanRange;

  const dotLabelMargin =
    !isNil(value) && value > maxRangeValue + difference / 2 ? -70 : null;

  const sourceLabel = !isNil(source)
    ? source === MarketDataSourceType.INDIGO_OFFERS
      ? 'Market data'
      : 'Comps'
    : undefined;

  const {
    isOpen: isSourceLabelLowOpen,
    onOpen: onSourceLabelLowOpen,
    onClose: onSourceLabelLowClose,
  } = useDisclosure();

  const {
    isOpen: isSourceLabelHighOpen,
    onOpen: onSourceLabelHighOpen,
    onClose: onSourceLabelHighClose,
  } = useDisclosure();

  const {
    isOpen: isTrackTooltipOpen,
    onOpen: onTrackTooltipOpen,
    onClose: onTrackTooltipClose,
  } = useDisclosure();

  const rangeDotPositions = highlightedRange
    ? highlightedRange.map((range) => {
        return calculateDotPosition(
          range,
          rangeValues[0],
          rangeValues[rangeValues.length - 1],
          true
        );
      })
    : [20, 80];

  // If we have a range track tooltip content, we don't want to show the slider thumbs
  const shouldShowSliderThumbTooltip = !getRangeTrackTooltipContent;

  return (
    <Flex
      alignItems="center"
      data-testid="key-terms-range-chart"
      gap={1}
      width="100%"
    >
      {valueLessThanRange && !isNil(value) && (
        <OutOfRangeSection
          dotColor={dotColor}
          isGreaterThanRange={valueGreaterThanRange}
          showValue={showValue}
          unit={unit}
          value={value}
        />
      )}
      <Flex
        direction="column"
        height="100%"
        justifyContent="center"
        width="100%"
      >
        <Flex alignItems="center">
          <Flex alignItems="center" position="relative" px={2} width="100%">
            {!isNil(value) && valueInRange && (
              <RangeDot
                color={dotColor}
                label={
                  showValue
                    ? formattedValue || ''
                    : isMobile
                      ? 'Offer'
                      : 'Your Offer'
                }
                labelMargin={dotLabelMargin}
                position={dotPosition}
                testId="key-terms-range-chart-offer-label"
                zIndex={2}
              />
            )}
            {anchor && (
              <RangeDot
                color="gray.400"
                label={anchor.label}
                position={anchorDotPosition}
                testId="key-terms-range-chart-list-price-label"
                zIndex={1}
                onClick={
                  getRangeTrackTooltipContent ? onTrackTooltipOpen : undefined
                }
                onMouseEnter={
                  getRangeTrackTooltipContent ? onTrackTooltipOpen : undefined
                }
                onMouseLeave={
                  getRangeTrackTooltipContent ? onTrackTooltipClose : undefined
                }
              />
            )}
            <RangeSlider
              aria-label={['min', 'max']}
              defaultValue={rangeDotPositions}
              isDisabled={true}
            >
              <RangeSliderTrack
                pointerEvents="auto"
                onClick={
                  getRangeTrackTooltipContent ? onTrackTooltipOpen : undefined
                }
                onMouseEnter={
                  getRangeTrackTooltipContent ? onTrackTooltipOpen : undefined
                }
                onMouseLeave={
                  getRangeTrackTooltipContent ? onTrackTooltipClose : undefined
                }
              >
                {getRangeTrackTooltipContent ? (
                  getRangeTrackTooltipContent({
                    children: <RangeSliderFilledTrack />,
                    isOpen: isTrackTooltipOpen,
                  })
                ) : (
                  <RangeSliderFilledTrack />
                )}
              </RangeSliderTrack>

              <RangeSliderThumbWithTooltip
                index={0}
                showTooltip={
                  shouldShowSliderThumbTooltip && isSourceLabelLowOpen
                }
                tooltipLabel={sourceLabel ? `${sourceLabel} low` : undefined}
                onTooltipClose={
                  shouldShowSliderThumbTooltip
                    ? onSourceLabelLowClose
                    : undefined
                }
                onTooltipOpen={
                  shouldShowSliderThumbTooltip
                    ? onSourceLabelLowOpen
                    : undefined
                }
              />
              <RangeSliderThumbWithTooltip
                index={1}
                showTooltip={
                  shouldShowSliderThumbTooltip && isSourceLabelHighOpen
                }
                tooltipLabel={sourceLabel ? `${sourceLabel} high` : undefined}
                onTooltipClose={
                  shouldShowSliderThumbTooltip
                    ? onSourceLabelHighClose
                    : undefined
                }
                onTooltipOpen={
                  shouldShowSliderThumbTooltip
                    ? onSourceLabelHighOpen
                    : undefined
                }
              />
            </RangeSlider>
          </Flex>
        </Flex>
        <Flex justifyContent="space-between">
          {rangeLabels.map((label) => (
            <Text key={label} fontSize={{ base: 'sm', md: 'md' }}>
              {label}
            </Text>
          ))}
        </Flex>
      </Flex>
      {valueGreaterThanRange && !isNil(value) && (
        <OutOfRangeSection
          dotColor={dotColor}
          isGreaterThanRange={valueGreaterThanRange}
          showValue={showValue}
          unit={unit}
          value={value}
        />
      )}
    </Flex>
  );
};
